import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form'
import FieldError, { RequiredMark } from './FieldError'
import { PlacesLookupType, type PlacesSuggestionResponse } from '@/helpers/api/WebApiClient'
import {
	PLACES_DEBOUNCE_MS,
	PLACES_MIN_INPUT_LENGTH,
	PLACES_INVALID_CITY_MESSAGE,
	PLACES_INVALID_POSTAL_MESSAGE,
	PLACES_TOO_MANY_MESSAGE,
	PLACES_UNAVAILABLE_MESSAGE,
	PlacesLookupError,
	confirmPlace,
	createPlacesSessionToken,
	debounce,
	searchPlaces,
	suggestionMatchesInput,
} from '@/helpers/googlePlaces'

interface PlacesAutocompleteFieldProps<TFieldValues extends FieldValues> {
	name: Path<TFieldValues>
	confirmedName: Path<TFieldValues>
	label: string
	type: PlacesLookupType
	countryId?: string
	stateId?: string
	city?: string
	disabled?: boolean
	required?: boolean
	placeholder?: string
	disabledPlaceholder?: string
	className?: string
	labelClassName?: string
	containerClass?: string
}

const PlacesAutocompleteField = <TFieldValues extends FieldValues>({
	name,
	confirmedName,
	label,
	type,
	countryId,
	stateId,
	city,
	disabled = false,
	required = false,
	placeholder,
	disabledPlaceholder,
	className,
	labelClassName,
	containerClass,
}: PlacesAutocompleteFieldProps<TFieldValues>) => {
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
	} = useFormContext<TFieldValues>()
	const [suggestions, setSuggestions] = useState<PlacesSuggestionResponse[]>([])
	const [defaultSuggestions, setDefaultSuggestions] = useState<PlacesSuggestionResponse[]>([])
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [lookupError, setLookupError] = useState<string | null>(null)
	const [activeIndex, setActiveIndex] = useState(-1)
	const [rateLimitedUntil, setRateLimitedUntil] = useState(0)
	const sessionTokenRef = useRef(createPlacesSessionToken())
	const requestIdRef = useRef(0)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const isPostal = type === PlacesLookupType.PostalCode
	const numericCountryId = Number(countryId)
	const numericStateId = Number(stateId)
	const canSearch = !disabled && numericCountryId > 0 && numericStateId > 0 && (!isPostal || Boolean(city?.trim()))
	const invalidMessage = isPostal ? PLACES_INVALID_POSTAL_MESSAGE : PLACES_INVALID_CITY_MESSAGE
	const constraintKey = `${countryId ?? ''}:${stateId ?? ''}:${isPostal ? city ?? '' : ''}:${type}`

	const applyLookupError = useCallback((error: unknown) => {
		const placesError = error instanceof PlacesLookupError ? error : new PlacesLookupError(PLACES_UNAVAILABLE_MESSAGE, 503)
		if (placesError.status === 429) {
			setRateLimitedUntil(Date.now() + 60_000)
		}
		setLookupError(placesError.message)
	}, [])

	useEffect(() => {
		sessionTokenRef.current = createPlacesSessionToken()
		setSuggestions([])
		setDefaultSuggestions([])
		setOpen(false)
		setLookupError(null)
		setActiveIndex(-1)

		if (!isPostal || !canSearch || Date.now() < rateLimitedUntil) {
			return
		}

		const currentValue = String(getValues(name) ?? '').trim()
		if (currentValue) {
			return
		}

		let cancelled = false
		const requestId = ++requestIdRef.current
		setLoading(true)
		void searchPlaces({
			input: '',
			type,
			countryId: numericCountryId,
			stateId: numericStateId,
			city,
			sessionToken: sessionTokenRef.current,
			defaults: true,
		})
			.then((nextSuggestions) => {
				if (cancelled || requestId !== requestIdRef.current) {
					return
				}

				setDefaultSuggestions(nextSuggestions)
				setSuggestions(nextSuggestions)
				setOpen(nextSuggestions.length > 0)
				setActiveIndex(nextSuggestions.length > 0 ? 0 : -1)
			})
			.catch((error) => {
				if (cancelled || requestId !== requestIdRef.current) {
					return
				}

				applyLookupError(error)
			})
			.finally(() => {
				if (!cancelled && requestId === requestIdRef.current) {
					setLoading(false)
				}
			})

		return () => {
			cancelled = true
		}
	}, [applyLookupError, canSearch, constraintKey, getValues, name, rateLimitedUntil])

	useEffect(() => {
		const onPointerDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false)
			}
		}

		document.addEventListener('mousedown', onPointerDown)
		return () => document.removeEventListener('mousedown', onPointerDown)
	}, [])

	const markUnconfirmed = useCallback(() => {
		setValue(confirmedName, false as TFieldValues[typeof confirmedName], { shouldDirty: true, shouldValidate: true })
	}, [confirmedName, setValue])

	const applySelection = useCallback(
		async (suggestion: PlacesSuggestionResponse) => {
			setLoading(true)
			setLookupError(null)
			try {
				const details = await confirmPlace({
					placeId: suggestion.placeId,
					type,
					countryId: numericCountryId,
					stateId: numericStateId,
					city,
					sessionToken: sessionTokenRef.current,
				})

				if (!details.isValid) {
					markUnconfirmed()
					setLookupError(invalidMessage)
					return
				}

				const nextValue = isPostal ? (details.postalCode || suggestion.mainText) : (details.city ?? suggestion.mainText)
				setValue(name, nextValue as TFieldValues[typeof name], { shouldDirty: true, shouldValidate: true })
				setValue(confirmedName, true as TFieldValues[typeof confirmedName], { shouldDirty: true, shouldValidate: true })
				setSuggestions([])
				setOpen(false)
				sessionTokenRef.current = createPlacesSessionToken()
			} catch (error) {
				applyLookupError(error)
				markUnconfirmed()
			} finally {
				setLoading(false)
			}
		},
		[applyLookupError, city, confirmedName, invalidMessage, isPostal, markUnconfirmed, name, numericCountryId, numericStateId, setValue, type]
	)

	const runSearch = useCallback(
		async (input: string) => {
			if (!canSearch || Date.now() < rateLimitedUntil) {
				if (Date.now() < rateLimitedUntil) {
					setLookupError(PLACES_TOO_MANY_MESSAGE)
				}
				setSuggestions([])
				return
			}

			const requestId = ++requestIdRef.current
			setLoading(true)
			setLookupError(null)
			try {
				const nextSuggestions = await searchPlaces({
					input,
					type,
					countryId: numericCountryId,
					stateId: numericStateId,
					city,
					sessionToken: sessionTokenRef.current,
				})
				if (requestId !== requestIdRef.current) {
					return
				}

				setSuggestions(nextSuggestions)
				setOpen(nextSuggestions.length > 0)
				setActiveIndex(nextSuggestions.length > 0 ? 0 : -1)
				if (nextSuggestions.length === 0) {
					setLookupError(invalidMessage)
					markUnconfirmed()
				}
			} catch (error) {
				if (requestId !== requestIdRef.current) {
					return
				}

				applyLookupError(error)
				setSuggestions([])
				setOpen(false)
			} finally {
				if (requestId === requestIdRef.current) {
					setLoading(false)
				}
			}
		},
		[applyLookupError, canSearch, city, invalidMessage, markUnconfirmed, numericCountryId, numericStateId, rateLimitedUntil, type]
	)

	const runSearchRef = useRef(runSearch)
	runSearchRef.current = runSearch

	const debouncedSearch = useMemo(
		() =>
			debounce((input: string) => {
				void runSearchRef.current(input)
			}, PLACES_DEBOUNCE_MS),
		[]
	)

	useEffect(() => {
		return () => debouncedSearch.cancel()
	}, [debouncedSearch])

	const confirmExactMatch = async (input: string) => {
		const match = suggestions.find((suggestion) => suggestionMatchesInput(suggestion, input))
		if (match) {
			await applySelection(match)
			return
		}

		if (getValues(confirmedName)) {
			return
		}

		if (input.trim()) {
			setLookupError(invalidMessage)
			markUnconfirmed()
		}
		setOpen(false)
	}

	const fieldError = lookupError || (errors?.[name as string]?.message as string | undefined) || (errors?.[confirmedName as string]?.message as string | undefined)

	return (
		<div className={containerClass ?? ''} ref={containerRef}>
			{label && (
				<label className={labelClassName ?? ''} htmlFor={String(name)}>
					{label}
					{required && <RequiredMark />}
				</label>
			)}
			<Controller
				name={name}
				control={control}
				render={({ field }) => (
					<div className="relative">
						<input
							{...field}
							id={String(name)}
							type="text"
							autoComplete="off"
							disabled={!canSearch}
							placeholder={!canSearch ? disabledPlaceholder : placeholder}
							className={`${className ?? ''} ${fieldError ? 'border-red-500' : ''}`}
							onChange={(event) => {
								field.onChange(event)
								markUnconfirmed()
								const value = event.target.value
								if (!value.trim()) {
									debouncedSearch.cancel()
									requestIdRef.current += 1
									setSuggestions(defaultSuggestions)
									setOpen(defaultSuggestions.length > 0)
									setLookupError(null)
									setLoading(false)
									return
								}
								if (value.trim().length < PLACES_MIN_INPUT_LENGTH) {
									debouncedSearch.cancel()
									requestIdRef.current += 1
									const query = value.trim().toLowerCase()
									const filtered = defaultSuggestions.filter(
										(suggestion) =>
											suggestionMatchesInput(suggestion, value) ||
											suggestion.mainText.toLowerCase().includes(query) ||
											suggestion.text.toLowerCase().includes(query)
									)
									setSuggestions(filtered)
									setOpen(filtered.length > 0)
									setLookupError(null)
									setLoading(false)
									return
								}
								debouncedSearch(value)
							}}
							onFocus={() => {
								if (suggestions.length > 0) {
									setOpen(true)
								} else if (defaultSuggestions.length > 0 && !String(field.value ?? '').trim()) {
									setSuggestions(defaultSuggestions)
									setOpen(true)
								}
							}}
							onBlur={() => {
								field.onBlur()
								window.setTimeout(() => {
									void confirmExactMatch(String(field.value ?? ''))
								}, 120)
							}}
							onKeyDown={(event) => {
								if (!open || suggestions.length === 0) {
									return
								}

								if (event.key === 'ArrowDown') {
									event.preventDefault()
									setActiveIndex((current) => (current + 1) % suggestions.length)
								} else if (event.key === 'ArrowUp') {
									event.preventDefault()
									setActiveIndex((current) => (current - 1 + suggestions.length) % suggestions.length)
								} else if (event.key === 'Enter' && activeIndex >= 0) {
									event.preventDefault()
									void applySelection(suggestions[activeIndex])
								} else if (event.key === 'Escape') {
									setOpen(false)
								}
							}}
						/>
						{loading && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-[hsl(var(--muted-foreground))]">...</span>}
						{open && suggestions.length > 0 && (
							<ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-lg" role="listbox">
								{suggestions.map((suggestion, index) => (
									<li key={suggestion.placeId}>
										<button
											type="button"
											role="option"
											aria-selected={index === activeIndex}
											className={`flex w-full flex-col px-3 py-2 text-left text-sm ${index === activeIndex ? 'bg-[hsl(var(--muted))]' : 'hover:bg-[hsl(var(--muted))]/70'}`}
											onMouseDown={(event) => event.preventDefault()}
											onClick={() => void applySelection(suggestion)}
										>
											<span className="font-medium text-[hsl(var(--foreground))]">{suggestion.mainText}</span>
											{suggestion.secondaryText && <span className="text-xs text-[hsl(var(--muted-foreground))]">{suggestion.secondaryText}</span>}
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			/>
			<FieldError message={fieldError} />
		</div>
	)
}

export default PlacesAutocompleteField
